import './App.css';
import React, { useEffect, useState } from 'react';
import DataPage from './components/DataPage';
import ChatPage from './components/ChatPage';
import PredictorPage from './components/PredictorPage';
import DesignerPage from './components/DesignerPage';
import AutoDataPage from './components/AutoDataPage';
import AutoSimulationPage from './components/AutoSimulationPage';
import TeamPage from './components/TeamPage';
import HomePage from './components/HomePage';
import NewsPage from './components/NewsPage';
import ToolsPage from './components/ToolsPage';
import ToolsHomePage from './components/ToolsHomePage';
import MixtureDensityPage from './components/MixtureDensityPage';
import MassAtomicConverterPage from './components/MassAtomicConverterPage';
import FeedbackWidget from './components/FeedbackWidget';

const MAIN_MENUS = [
  { key: 'overview', label: 'Overview', submenus: [] },
  { key: 'base', label: 'MATAI Core', submenus: ['Database', 'Predictor', 'Designer'] },
  // { key: 'pro', label: 'MATAI Pro', submenus: ['Data Extraction', 'Auto Simulation', 'Matai Chat'] },
  // Temporarily hidden: restore the above entry to re-enable the MATAI Pro module in the sidebar.
  {
    key: 'tools',
    label: 'MATAI Tools',
    submenus: ['Offset Yield Strength', 'Mixture Density', 'Mass-Atomic Converter', 'Data Extraction'],
  },
  { key: 'chat', label: 'MATAI Chat', submenus: [] },
  { key: 'news', label: 'News', submenus: [] },
  { key: 'team', label: 'Team', submenus: [] },
];

const MAIN_MENU_PATHS = {
  overview: '/',
  base: '/core',
  tools: '/tools',
  chat: '/matai-chat',
  news: '/news',
  team: '/team',
};

const SUBMENU_PATHS = {
  base: {
    Database: '/core/database',
    Predictor: '/core/predictor',
    Designer: '/core/designer',
  },
  tools: {
    'Offset Yield Strength': '/tools/offset-yield-strength',
    'Mixture Density': '/tools/mixture-density',
    'Mass-Atomic Converter': '/tools/mass-atomic-converter',
    'Data Extraction': '/tools/data-extraction',
  },
};

const PATH_TO_ROUTE = {
  '/': { mainMenu: 'overview', subMenu: '' },
  '/news': { mainMenu: 'news', subMenu: '' },
  '/team': { mainMenu: 'team', subMenu: '' },
  '/teams': { mainMenu: 'team', subMenu: '' },
  '/tools': { mainMenu: 'tools', subMenu: '' },
  '/matai-chat': { mainMenu: 'chat', subMenu: '' },
  '/core': { mainMenu: 'base', subMenu: 'Database' },
  '/core/database': { mainMenu: 'base', subMenu: 'Database' },
  '/core/predictor': { mainMenu: 'base', subMenu: 'Predictor' },
  '/core/designer': { mainMenu: 'base', subMenu: 'Designer' },
  '/tools/offset-yield-strength': { mainMenu: 'tools', subMenu: 'Offset Yield Strength' },
  '/tools/mixture-density': { mainMenu: 'tools', subMenu: 'Mixture Density' },
  '/tools/mass-atomic-converter': { mainMenu: 'tools', subMenu: 'Mass-Atomic Converter' },
  '/tools/data-extraction': { mainMenu: 'tools', subMenu: 'Data Extraction' },
};

const normalizePath = (path) => {
  if (!path) return '/';
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
};

const getRouteFromPath = (path) => {
  const normalized = normalizePath(path);
  return PATH_TO_ROUTE[normalized] || PATH_TO_ROUTE['/'];
};

const getPathFromRoute = (mainMenu, subMenu) => {
  if (subMenu && SUBMENU_PATHS[mainMenu] && SUBMENU_PATHS[mainMenu][subMenu]) {
    return SUBMENU_PATHS[mainMenu][subMenu];
  }
  if (MAIN_MENU_PATHS[mainMenu]) {
    return MAIN_MENU_PATHS[mainMenu];
  }
  return '/';
};

const getExpandedMenusForRoute = (route) => {
  const menu = MAIN_MENUS.find(m => m.key === route.mainMenu);
  if (menu && menu.submenus.length > 0) {
    return [menu.key];
  }
  return [];
};

function App() {
  const initialRoute = getRouteFromPath(window.location.pathname);
  const [mainMenu, setMainMenu] = useState(initialRoute.mainMenu);
  const [subMenu, setSubMenu] = useState(initialRoute.subMenu);
  const [expandedMenus, setExpandedMenus] = useState(() => getExpandedMenusForRoute(initialRoute));
  const [defaultTabIndex, setDefaultTabIndex] = useState(0);

  useEffect(() => {
    const handlePopState = () => {
      const route = getRouteFromPath(window.location.pathname);
      setMainMenu(route.mainMenu);
      setSubMenu(route.subMenu);
      setExpandedMenus(getExpandedMenusForRoute(route));
      setDefaultTabIndex(0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const updatePath = (nextMainMenu, nextSubMenu) => {
    const nextPath = getPathFromRoute(nextMainMenu, nextSubMenu);
    if (normalizePath(window.location.pathname) !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
  };

  const handleMainMenuClick = (key) => {
    setMainMenu(key);
    const menu = MAIN_MENUS.find(m => m.key === key);
    
    if (menu && menu.submenus.length > 0) {
      if (key === 'tools') {
        setSubMenu('');
        updatePath(key, '');
        setExpandedMenus(prev =>
          prev.includes(key)
            ? prev.filter(k => k !== key)
            : [...prev, key]
        );
        return;
      }
      const nextSubMenu = menu.submenus[0];
      setSubMenu(nextSubMenu);
      updatePath(key, nextSubMenu);
      setExpandedMenus(prev => 
        prev.includes(key) 
          ? prev.filter(k => k !== key)
          : [...prev, key]
      );
    } else {
      setSubMenu('');
      updatePath(key, '');
    }
  };

  const handleSubMenuClick = (submenu, parentKey, tabIndex) => {
    setSubMenu(submenu);
    setMainMenu(parentKey);
    if (!expandedMenus.includes(parentKey)) {
      setExpandedMenus(prev => [...prev, parentKey]);
    }
    updatePath(parentKey, submenu);
    // 如果有tabIndex参数，存储它以便传递给组件
    if (tabIndex !== undefined) {
      setDefaultTabIndex(tabIndex);
    } else {
      // 重置为默认值
      setDefaultTabIndex(0);
    }
  };

  const renderContent = () => {
    if (mainMenu === 'overview') {
      return <HomePage onNavigate={handleSubMenuClick} />;
    }
    
    if (mainMenu === 'team') {
      return <TeamPage />;
    }
    if (mainMenu === 'news') {
      return <NewsPage />;
    }
    if (mainMenu === 'chat') {
      return <ChatPage />;
    }
    if (mainMenu === 'tools') {
      if (!subMenu) {
        return <ToolsHomePage onNavigate={handleSubMenuClick} />;
      }
    }
    
    switch (subMenu) {
      case 'Offset Yield Strength':
        return <ToolsPage />;
      case 'Mixture Density':
        return <MixtureDensityPage />;
      case 'Mass-Atomic Converter':
        return <MassAtomicConverterPage />;
      case 'Database':
        return <DataPage onNavigate={handleSubMenuClick} defaultTabIndex={defaultTabIndex} />;
      case 'Predictor':
        return <PredictorPage />;
      case 'Designer':
        return <DesignerPage />;
      case 'Data Extraction':
        return <AutoDataPage />;
      case 'Auto Simulation':
        return <AutoSimulationPage onNavigate={handleSubMenuClick} />;
      default:
        return <DesignerPage />;
    }
  };

  return (
    <div className="matai-layout">
      <div className="matai-sidebar">
        <div className="matai-logo">
          <span className="matai-logo-text">MATAI</span>
        </div>
        <div className="matai-menu">
          {MAIN_MENUS.map(menu => (
            <div key={menu.key} className="matai-menu-group">
              <div
                className={`matai-menu-item${mainMenu === menu.key ? ' selected' : ''}`}
                onClick={() => handleMainMenuClick(menu.key)}
              >
                {menu.label}
                {menu.submenus.length > 0 && (
                  <span className={`menu-arrow ${expandedMenus.includes(menu.key) ? 'expanded' : ''}`}>▼</span>
                )}
              </div>
              {expandedMenus.includes(menu.key) && menu.submenus.length > 0 && (
                <div className="matai-submenu">
                  {menu.submenus.map((submenu, idx) =>
                    submenu ? (
                      <div
                        key={submenu}
                        className={`matai-submenu-item${subMenu === submenu ? ' selected' : ''}`}
                        onClick={() => handleSubMenuClick(submenu, menu.key)}
                      >
                        {submenu}
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="matai-content">
        <div className="matai-content-body">
          {renderContent()}
        </div>
      </div>
      <FeedbackWidget />
    </div>
  );
}

export default App;
